import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axiosInstance from '../../api/api';
import { API_ROUTES } from '../../config/api-routes';
import { uploadMessagingAttachment } from '../../api/uploads';
import { isNodeBackend, supportsDedicatedChatSocket } from '../../utils/backendConfig';
import { normalizeApiMessage, type NormalizedChatMessage } from '../../utils/normalizeApiMessage';
import {
  filterQuestionThreadMessages,
  hubQuestionToThreadRef,
  sanitizeQuestionThreadMessages,
  sortThreadMessages,
  type QuestionThreadRef,
} from '../../utils/questionThreadMessages';
import {
  createPersistentChatSocket,
  formatWsMessage,
  sendChatPayload,
  type PersistentChatSocket,
} from '../../utils/realtime';
import { getSocket } from '../../utils/sockets';
import type { V1SocketPayload } from '../../utils/sockets';
import { toast } from 'react-toastify';
import { useUserStore } from '../../store/userStore';
import type { HubAttachment } from './AttachmentView';

export type QuestionThreadSource = {
  id: string;
  question: string;
  section: string;
  questionItems?: Array<{ section?: string; questionText?: string }>;
  reply?: string;
  replyAttachments?: HubAttachment[];
  conversationId?: string;
};

export function useQuestionThread(question: QuestionThreadSource | null, candidateUserId?: number) {
  const user = useUserStore((state) => state.user);
  const senderId = candidateUserId ?? user?.id;
  const conversationId = question?.conversationId || '';
  const threadRef = useMemo(() => (question ? hubQuestionToThreadRef(question) : null), [question]);
  const [messages, setMessages] = useState<NormalizedChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isSending, setIsSending] = useState(false);
  const chatConnectionRef = useRef<PersistentChatSocket | null>(null);

  const isCandidateMessage = useCallback(
    (message: NormalizedChatMessage) => {
      if (senderId && Number(message.senderId) === Number(senderId)) {
        return true;
      }
      return message.sender?.role === 'candidate';
    },
    [senderId]
  );

  const upsertMessage = useCallback(
    (raw: Record<string, unknown>) => {
      if (!question) return;
      const normalized = normalizeApiMessage(raw);
      if (String(normalized.chatRequestId || '') !== String(question.id)) return;
      setMessages((prev) => {
        const withoutPending = prev.filter(
          (row) =>
            !String(row.id).startsWith('temp-') &&
            !(row.pending && row.senderId === normalized.senderId)
        );
        const base = sanitizeQuestionThreadMessages(withoutPending);
        const index = base.findIndex((row) => String(row.id) === String(normalized.id));
        if (index >= 0) {
          const next = [...base];
          next[index] = normalized;
          return sortThreadMessages(next);
        }
        return sortThreadMessages([...base, normalized]);
      });
    },
    [question]
  );

  const loadMessages = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!conversationId) {
        setMessages([]);
        return;
      }
      if (!opts?.silent) {
        setLoading(true);
      }
      try {
        const url = API_ROUTES.MESSAGESHUB.GET_MESSAGES.replace(':conversationId', conversationId);
        const response = await axiosInstance.get(url);
        if (response.data?.success && Array.isArray(response.data.data)) {
          const rows = response.data.data as Record<string, unknown>[];
          setMessages(sanitizeQuestionThreadMessages(rows.map((row) => normalizeApiMessage(row))));
        } else {
          setMessages([]);
        }
      } catch (error) {
        console.error('Failed to load question thread', error);
        setMessages([]);
      } finally {
        if (!opts?.silent) {
          setLoading(false);
        }
      }
    },
    [conversationId]
  );

  useEffect(() => {
    if (!question?.id) {
      setMessages([]);
      return;
    }
    void loadMessages();
  }, [loadMessages, question?.id]);

  useEffect(() => {
    if (!conversationId || !supportsDedicatedChatSocket()) {
      chatConnectionRef.current?.disconnect();
      chatConnectionRef.current = null;
      return;
    }

    const connection = createPersistentChatSocket(conversationId, {
      onMessage: (raw) => {
        const formatted = formatWsMessage(raw);
        upsertMessage({
          id: formatted.id,
          senderId: formatted.senderId,
          content: formatted.content,
          createdAt: formatted.createdAt,
          status: formatted.status,
          kind: formatted.kind,
          chatRequestId: formatted.chatRequestId,
          attachments: formatted.attachments,
          sender: formatted.sender,
        });
      },
    });
    if (!connection) {
      chatConnectionRef.current = null;
      return;
    }
    chatConnectionRef.current = connection;

    return () => {
      connection.disconnect();
      if (chatConnectionRef.current === connection) {
        chatConnectionRef.current = null;
      }
    };
  }, [conversationId, upsertMessage]);

  useEffect(() => {
    if (!conversationId || !senderId || !question?.id) return;

    const socket = getSocket();
    const conversationIds = new Set([conversationId].filter(Boolean).map((id) => id.toLowerCase()));

    const matchesConversation = (payload: V1SocketPayload) => {
      const convId = String(
        payload.conversationUuid || payload.conversationId || payload.conversation_id || ''
      ).toLowerCase();
      return !convId || conversationIds.has(convId);
    };

    const onNewMessage = (payload: V1SocketPayload) => {
      if (!matchesConversation(payload)) return;
      const embedded = payload.data as Record<string, unknown> | undefined;
      if (embedded?.id) {
        upsertMessage(embedded);
        return;
      }
      void loadMessages({ silent: true });
    };

    socket.on('newMessage', onNewMessage);
    return () => {
      socket.off('newMessage', onNewMessage);
    };
  }, [conversationId, senderId, loadMessages, question?.id, upsertMessage]);

  const threadMessages = useMemo(() => {
    if (!threadRef) return [];
    return filterQuestionThreadMessages(messages, threadRef, isCandidateMessage);
  }, [messages, threadRef, isCandidateMessage]);

  const handleSendFollowUp = async () => {
    if (
      !question ||
      (!reply.trim() && selectedFiles.length === 0) ||
      !conversationId ||
      isSending
    ) {
      return;
    }

    const messageText = reply.trim();
    const filesToSend = [...selectedFiles];
    const tempId = `temp-${Date.now()}`;

    setReply('');
    setSelectedFiles([]);

    if (senderId && user) {
      upsertMessage({
        id: tempId,
        senderId: senderId,
        content: messageText,
        createdAt: new Date().toISOString(),
        status: 'sent',
        chatRequestId: question.id,
        pending: true,
        attachments: [],
        sender: {
          id: senderId,
          firstName: user.firstName ?? null,
          lastName: user.lastName ?? null,
          email: user.email ?? '',
          profileImg: user.profileImg ?? null,
          role: 'candidate',
        },
      });
    }

    const revertComposer = () => {
      setReply(messageText);
      setSelectedFiles(filesToSend);
      setMessages((prev) => prev.filter((row) => String(row.id) !== tempId));
    };

    try {
      setIsSending(true);
      let attachmentUrl: string | undefined;
      let attachmentType: string | undefined;

      if (filesToSend.length > 0) {
        const file = filesToSend[0];
        const purpose = file.type.startsWith('image/')
          ? 'chat_image'
          : file.type.startsWith('audio/')
            ? 'chat_audio'
            : 'chat_file';
        const uploaded = await uploadMessagingAttachment(file, purpose);
        attachmentUrl = uploaded.publicUrl;
        attachmentType = uploaded.attachmentType;
      }

      const payload = {
        content: messageText,
        ...(attachmentUrl
          ? {
              attachment_url: attachmentUrl,
              attachment_type: attachmentType,
            }
          : {}),
        chatRequestId: question.id,
      };

      const chatWs = chatConnectionRef.current?.getSocket() ?? null;
      if (chatWs?.readyState === WebSocket.OPEN) {
        sendChatPayload(chatWs, payload);
        setIsSending(false);
        return;
      }

      const res = await axiosInstance.post(API_ROUTES.MESSAGESHUB.SEND_MESSAGE(conversationId), {
        messageText,
        content: messageText,
        attachment_url: attachmentUrl,
        attachment_type: attachmentType,
        chatRequestId: question.id,
      });

      if (res.data?.success) {
        const saved = res.data.data as Record<string, unknown> | undefined;
        if (saved?.id) {
          upsertMessage(saved);
        } else if (!isNodeBackend()) {
          void loadMessages({ silent: true });
        }
        return;
      }

      throw new Error(res.data?.message || 'Failed to send message');
    } catch (error: unknown) {
      revertComposer();
      const message =
        error instanceof Error
          ? error.message
          : (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            'Failed to send message';
      toast.error(message);
    } finally {
      setIsSending(false);
    }
  };

  return {
    active: Boolean(question?.conversationId),
    threadRef: threadRef as QuestionThreadRef | null,
    threadMessages,
    loading,
    reply,
    setReply,
    selectedFiles,
    setSelectedFiles,
    isSending,
    isCandidateMessage,
    handleSendFollowUp,
  };
}
